import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'

import { getLocalZipCodeAndCity } from '../../services/localStorageService'
import { redirectLocation } from '../../services/urls'
import { managerEvents } from './App'
import { DatasetSchema } from './validation'
import { pushGTMFormattedDataLayer } from '../../services/gtmIzi'

type State = 'zipcode' | 'qr' | 'endform'

interface Props {
  dataset: DatasetSchema
}

export default function QrManager({ dataset }: Props) {
  const { zipCode: localZipCode, city: localCity } = getLocalZipCodeAndCity()
  const canLoadZipCode = !!dataset?.iziZipcodeCss && !!dataset?.iziZipcodeJs
  const shouldLoadZipCode = (!localZipCode || !localCity || dataset.restartQr) && canLoadZipCode
  const [state, setState] = useState<State>(shouldLoadZipCode ? 'zipcode' : 'qr')

  if (dataset.zipCode) {
    window.localStorage.setItem('zipCode', dataset.zipCode)
  }

  useEffect(() => {
    if (state === 'zipcode') {
      const handleZipcodeNext = () => {
        pushGTMFormattedDataLayer(`parcours|start-qr|${dataset.slug}|${dataset.parcoursType ?? 'parcours_qr'}`, true)

        setState('qr')
      }

      window.addEventListener(managerEvents.zipcode.next.type, handleZipcodeNext)

      return () => {
        window.removeEventListener(managerEvents.zipcode.next.type, handleZipcodeNext)
      }
    } else if (state === 'qr') {
      const handleQrRestart = () => {
        if (canLoadZipCode) {
          setState('zipcode')
        } else {
          setState('qr')
        }
      }

      const handleQrPrev = () => {
        if (canLoadZipCode) {
          setState('zipcode')
        } else {
          redirectLocation(dataset.fallbackUrl)
        }
      }

      const handleQrNext = () => {
        dataset.iziEndRedirect ? redirectLocation(dataset.iziEndRedirect) : setState('endform')
      }

      window.addEventListener(managerEvents.qr.restart.type, handleQrRestart)
      window.addEventListener(managerEvents.qr.prev.type, handleQrPrev)
      window.addEventListener(managerEvents.qr.next.type, handleQrNext)

      return () => {
        window.removeEventListener(managerEvents.qr.restart.type, handleQrRestart)
        window.removeEventListener(managerEvents.qr.prev.type, handleQrPrev)
        window.removeEventListener(managerEvents.qr.next.type, handleQrNext)
      }
    } else {
      const handleQrPrev = () => {
        pushGTMFormattedDataLayer(`parcours|qr-retour|formulaire|${dataset.parcoursType}|${dataset.slug}`)
        setState('qr')
      }

      window.addEventListener(managerEvents.endform.prev.type, handleQrPrev)

      return () => {
        window.removeEventListener(managerEvents.endform.prev.type, handleQrPrev)
      }
    }
  }, [state])

  return (
    <>
      <Helmet>
        {dataset.iziZipcodeCss && <link href={dataset.iziZipcodeCss} rel="stylesheet" crossOrigin="anonymous" />}
        <link href={dataset.iziQrCss} rel="stylesheet" crossOrigin="anonymous" />
        {dataset.iziEndformCss && <link href={dataset.iziEndformCss} rel="stylesheet" crossOrigin="anonymous" />}
      </Helmet>
      {state === 'zipcode' ? (
        <Helmet>
          <script src={dataset.iziZipcodeJs} type="module" crossOrigin="anonymous" async />
        </Helmet>
      ) : state === 'qr' ? (
        <Helmet>
          <script src={dataset.iziQrJs} type="module" crossOrigin="anonymous" async />
        </Helmet>
      ) : (
        <Helmet>
          <script src={dataset.iziEndformJs} type="module" crossOrigin="anonymous" async />
        </Helmet>
      )}

      {dataset.iziZipcodeJs && (
        <div
          style={{ height: '100%', backgroundColor: 'inherit', display: state === 'zipcode' ? 'block' : 'none' }}
          id="qr-zipcode"
          data-slug={dataset.slug}
          data-max-step={dataset.maxStep}
          data-parcours-type={dataset.parcoursType}
          data-zip-code={dataset.zipCode}
          data-fallback-url={dataset.fallbackUrl}
          data-show-exit-cross={dataset.showExitCross}
          data-show-logo={dataset.showLogo}
          data-show-title={dataset.showTitle}
          data-thumbnail={dataset.thumbnail}
          data-logo-partner={dataset.logoPartner}
        />
      )}
      <div
        style={{ height: '100%', backgroundColor: 'inherit', display: state === 'qr' ? 'block' : 'none' }}
        id="qr-izi"
        data-slug={dataset.slug}
        data-max-step={dataset.maxStep}
        data-hide-progress-bar-in-first-question={dataset.hideProgressBarInFirstQuestion}
        data-parcours-type={dataset.parcoursType}
        data-fallback-url={dataset.fallbackUrl}
        data-show-exit-cross={dataset.showExitCross}
        data-show-logo={dataset.showLogo}
        data-show-title={dataset.showTitle}
        data-show-summary={dataset.showSummary}
        data-numbered-questions={dataset.numberedQuestions}
        data-restart-qr={dataset.restartQr}
        data-thumbnail={dataset.thumbnail}
        data-logo-partner={dataset.logoPartner}
        data-show-first-return={dataset.showFirstReturn}
      />
      {dataset.iziEndformJs && (
        <div
          style={{ height: '100%', backgroundColor: 'inherit', display: state === 'endform' ? 'block' : 'none' }}
          id="qr-endform"
          data-slug={dataset.slug}
          data-zip-code={dataset.zipCode}
          data-parcours-type={dataset.parcoursType}
          data-fallback-url={dataset.fallbackUrl}
          data-partner={dataset.partner}
          data-disable-callback-date={dataset.disableCallbackDate}
          data-show-exit-cross={dataset.showExitCross}
          data-show-logo={dataset.showLogo}
          data-show-title={dataset.showTitle}
          data-thumbnail={dataset.thumbnail}
          data-send-to-lead-data={JSON.stringify(dataset.sendToLeadData)}
          data-logo-partner={dataset.logoPartner}
          data-end-upload-tab={dataset.endUploadTab}
        />
      )}
    </>
  )
}
